import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/auth/api';
import { config } from '@/config';
import { tokenStore } from '@/auth/token-store';
import type { FleetBillingStatus } from '@breaker/shared-types';

/** Mirror of the API's derived BillingView (P2) — never stored client-side. */
export interface BillingView {
  status: FleetBillingStatus;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  wall: boolean;
  graceEndsAt: string | null;
  vatEnabled: boolean;
  /** 0084: outside billing entirely — render "Founding — no charge" and no
   *  pay CTA. Read THIS, not `status`: an exempt fleet still reports
   *  'trialing' honestly, because that is what its columns say. */
  exempt: boolean;
}

export interface BillingState {
  billing: BillingView;
  seats: number;
  pricePerSeatPence: number;
  hasCard: boolean;
  hasSubscription: boolean;
  available: boolean;
}

export function useBilling(fleetId: string | null) {
  return useQuery({
    queryKey: ['billing', fleetId ?? ''] as const,
    enabled: fleetId !== null,
    queryFn: () => api<BillingState>(`/v1/fleets/${fleetId}/billing`),
    // Billing state moves via webhooks — keep it reasonably fresh while the
    // page is open (a checkout return should reflect quickly).
    refetchInterval: 30_000,
    // Drivers/accountants may 403 the read; that's "no billing UI", not an
    // error worth retrying.
    retry: false,
  });
}

/** Both money actions answer { url } — a hosted Stripe page to hand off to. */
export function useCheckoutSession(fleetId: string | null) {
  return useMutation({
    mutationFn: () =>
      api<{ url: string }>(`/v1/fleets/${fleetId}/billing/checkout-session`, {
        method: 'POST',
      }),
    onSuccess: ({ url }) => window.location.assign(url),
  });
}

export function usePortalSession(fleetId: string | null) {
  return useMutation({
    mutationFn: () =>
      api<{ url: string }>(`/v1/fleets/${fleetId}/billing/portal-session`, {
        method: 'POST',
      }),
    onSuccess: ({ url }) => window.location.assign(url),
  });
}

/**
 * The CSV bundle needs an Authorization header, so it can't be a plain
 * <a href> — fetch to a blob and hand the browser an object URL instead.
 */
export async function downloadExport(fleetId: string): Promise<void> {
  const res = await fetch(config.apiUrl + `/v1/fleets/${fleetId}/export`, {
    headers: { authorization: `Bearer ${tokenStore.getAccess() ?? ''}` },
  });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const name =
    res.headers.get('content-disposition')?.match(/filename="([^"]+)"/)?.[1] ??
    'breaker-export.zip';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}
